import React, { useState, useEffect, useRef } from 'react';
import {
  MessageBox,
  Input,
  Button as ChatButton,
  MessageList
} from 'react-chat-elements';
import 'react-chat-elements/dist/main.css';
import { useAuth } from 'context/AuthContext';
import axios from 'axios';
import ArgonBox from 'components/ArgonBox';
import ArgonButton from 'components/ArgonButton';
import ArgonTypography from 'components/ArgonTypography';
import {
  Icon,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  Badge
} from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import ChatWebSocket from './ChatWebSocket';

const ChatSupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [activeConversations, setActiveConversations] = useState([]);
  const [showConversationList, setShowConversationList] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const messageListRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const lastMessageTimestampRef = useRef(null);
  const { idToken, userId, isAuthenticated } = useAuth();

  // Initialize WebSocket connection when widget opens
  useEffect(() => {
    if (isOpen && isAuthenticated && idToken && !wsRef.current) {
      // Try WebSocket first, but don't block the UI if it fails
      initializeWebSocket();

      // Also set up REST API polling as fallback for real-time updates
      setupPollingFallback();
    } else if (!isOpen && wsRef.current) {
      // Disconnect WebSocket when widget closes
      wsRef.current.disconnect();
      wsRef.current = null;
      setWsConnected(false);

      // Clear polling if active
      clearPollingFallback();
    }

    return () => {
      // Cleanup on unmount
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }
      clearPollingFallback();
    };
  }, [isOpen, isAuthenticated, idToken]);

  // Initialize conversation on first open
  useEffect(() => {
    if (isOpen && !conversationId && isAuthenticated) {
      initializeConversation();
    }
  }, [isOpen, conversationId, isAuthenticated]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // WebSocket Functions
  const initializeWebSocket = () => {
    if (wsRef.current) return;

    console.log('🔌 Initializing WebSocket for user chat', {
      userId: userId,
      hasToken: !!idToken,
      isAuthenticated: isAuthenticated,
      conversationId: conversationId,
      activeConversationsCount: activeConversations.length
    });

    // Test if WebSocket endpoint exists by checking if the base URL is accessible
    console.log('🔍 Testing WebSocket endpoint availability...');

    wsRef.current = new ChatWebSocket(
      'wss://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev',
      idToken
    );

    // Set up WebSocket event handlers
    wsRef.current.setMessageHandler(handleWebSocketMessage);
    wsRef.current.setTypingHandler(handleWebSocketTyping);
    wsRef.current.setStatusUpdateHandler(handleWebSocketStatusUpdate);
    wsRef.current.setConnectionChangeHandler(handleWebSocketConnectionChange);
    wsRef.current.setErrorHandler(handleWebSocketError);

    // Connect as user - v2.0 doesn't require organizationId for users
    wsRef.current.connectAsUser();
  };

  const handleWebSocketMessage = (messageData) => {
    console.log('📨 User received real-time message:', {
      messageData,
      currentConversationId: conversationId,
      userId: userId,
      isWidgetOpen: isOpen
    });
    const { conversationId: msgConversationId, message, sender } = messageData;

    // Update conversation list - move conversation to top and update metadata
    setActiveConversations((prev) => {
      const updated = prev.map((conv) => {
        const convId = conv._id || conv.id;
        if (convId === msgConversationId) {
          return {
            ...conv,
            lastMessageAt: message.createdAt || new Date().toISOString(),
            lastMessage: message.content,
            messageCount: (conv.messageCount || 0) + 1
          };
        }
        return conv;
      });

      // Sort by lastMessageAt to move updated conversation to top
      return updated.sort(
        (a, b) =>
          new Date(b.lastMessageAt || b.updatedAt || b.createdAt) -
          new Date(a.lastMessageAt || a.updatedAt || a.createdAt)
      );
    });

    // If user is viewing this conversation, add message to chat
    if (conversationId === msgConversationId) {
      const formattedMessage = {
        position: message.senderId === userId ? 'right' : 'left',
        type: 'text',
        text: message.content,
        date: new Date(message.timestamp || message.createdAt),
        title: message.senderId === userId ? 'You' : 'Support'
      };

      setMessages((prev) => [...prev, formattedMessage]);
    } else {
      // Show notification for messages in other conversations
      if (message.senderId !== userId && sender) {
        console.log(
          `💬 New message from ${
            sender.name || 'Support'
          } in another conversation`
        );
        // Could implement browser notifications here if needed
      }
    }
  };

  const handleWebSocketTyping = (typingData) => {
    console.log('⌨️ Received typing indicator:', typingData);
    const {
      conversationId: typingConversationId,
      senderId,
      senderType,
      isTyping
    } = typingData;

    // Only show typing indicators for current conversation and from other users
    if (conversationId === typingConversationId && senderId !== userId) {
      setTypingUsers((prev) => {
        const newTypingUsers = new Set(prev);
        if (isTyping) {
          newTypingUsers.add(senderId);
        } else {
          newTypingUsers.delete(senderId);
        }
        return newTypingUsers;
      });

      // Auto-clear typing indicator after 3 seconds
      if (isTyping) {
        setTimeout(() => {
          setTypingUsers((prev) => {
            const newTypingUsers = new Set(prev);
            newTypingUsers.delete(senderId);
            return newTypingUsers;
          });
        }, 3000);
      }
    }
  };

  const handleWebSocketStatusUpdate = (statusData) => {
    console.log('📊 Received status update:', statusData);
    const { conversationId: statusConversationId, field, value } = statusData;

    // Update conversation status in local state
    setActiveConversations((prev) =>
      prev.map((conv) => {
        const convId = conv._id || conv.id;
        return convId === statusConversationId
          ? { ...conv, [field]: value }
          : conv;
      })
    );
  };

  const handleWebSocketConnectionChange = (
    connected,
    shouldReconnect = false
  ) => {
    console.log('🔌 User WebSocket connection changed:', {
      connected,
      shouldReconnect,
      conversationId,
      userId
    });
    setWsConnected(connected);

    if (connected) {
      console.log(
        '✅ User WebSocket connected successfully - ready to receive messages'
      );
    } else {
      console.log(
        '❌ User WebSocket disconnected - real-time updates disabled'
      );
    }

    if (shouldReconnect && !connected) {
      // Attempt to reinitialize WebSocket after a delay
      setTimeout(() => {
        if (isOpen && isAuthenticated && idToken) {
          console.log('🔄 Attempting to reconnect WebSocket...');
          initializeWebSocket();
        }
      }, 2000);
    }
  };

  const handleWebSocketError = (error) => {
    console.error('❌ WebSocket error:', error);
    setWsConnected(false);
  };

  // REST API Polling Fallback Functions
  const setupPollingFallback = () => {
    if (pollingIntervalRef.current) return;

    console.log(
      '🔄 Setting up REST API polling fallback for real-time updates'
    );

    // Poll every 3 seconds when WebSocket is not connected
    pollingIntervalRef.current = setInterval(async () => {
      if (!wsConnected && conversationId && isOpen) {
        await pollForNewMessages();
      }
    }, 3000);
  };

  const clearPollingFallback = () => {
    if (pollingIntervalRef.current) {
      console.log('🛑 Clearing REST API polling fallback');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const pollForNewMessages = async () => {
    if (!conversationId || !idToken) return;

    try {
      const response = await axios.get(
        `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/users/conversations/${conversationId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            limit: 5,
            page: 1,
            // Only get messages after the last known timestamp
            ...(lastMessageTimestampRef.current && {
              after: lastMessageTimestampRef.current
            })
          }
        }
      );

      const newMessages = response.data.data || [];

      if (newMessages.length > 0) {
        console.log('📨 Polling found new messages:', newMessages.length);

        // Sort messages by timestamp
        const sortedMessages = newMessages.sort(
          (a, b) =>
            new Date(a.timestamp || a.createdAt) -
            new Date(b.timestamp || b.createdAt)
        );

        // Add new messages to the existing ones
        const formattedNewMessages = sortedMessages.map((msg) => ({
          position: msg.senderId === userId ? 'right' : 'left',
          type: 'text',
          text: msg.content,
          date: new Date(msg.timestamp || msg.createdAt),
          title: msg.senderId === userId ? 'You' : 'Support'
        }));

        // Update messages state
        setMessages((prev) => {
          // Avoid duplicates by checking timestamps
          const existingTimestamps = new Set(prev.map((m) => m.date.getTime()));
          const uniqueNewMessages = formattedNewMessages.filter(
            (m) => !existingTimestamps.has(m.date.getTime())
          );

          if (uniqueNewMessages.length > 0) {
            // Update last message timestamp
            const latestMessage = sortedMessages[sortedMessages.length - 1];
            lastMessageTimestampRef.current =
              latestMessage.timestamp || latestMessage.createdAt;

            return [...prev, ...uniqueNewMessages];
          }
          return prev;
        });
      }
    } catch (error) {
      // Silently handle polling errors to avoid spam
      console.debug(
        'Polling error (normal if no new messages):',
        error.message
      );
    }
  };

  const fetchActiveConversations = async () => {
    if (!idToken) return [];

    try {
      // Use the new v2.0 user endpoint
      const response = await axios.get(
        `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/users/conversations`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            limit: 10,
            page: 1
          }
        }
      );

      // Extract conversations from the response data structure
      console.log('Full response:', response.data);
      const allConversations = response.data.data || response.data || [];
      console.log('Fetched conversations:', allConversations);

      // Filter for active conversations (not closed)
      const activeConversations = allConversations.filter(
        (conv) => conv.state === 'open' || conv.state === 'waiting_for_user'
      );
      console.log('Active conversations:', activeConversations);

      // Sort by most recent activity
      activeConversations.sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt) -
          new Date(a.updatedAt || a.createdAt)
      );

      setActiveConversations(activeConversations);
      return activeConversations;
    } catch (error) {
      console.error('Failed to fetch active conversations:', error);
      setActiveConversations([]);
      return [];
    }
  };

  const initializeConversation = async () => {
    try {
      setIsLoading(true);

      // First, fetch active conversations for the user
      const conversations = await fetchActiveConversations();

      if (conversations.length > 0) {
        // Conversations found - use the most recent active conversation
        const mostRecentConversation = conversations[0];
        console.log('Setting conversation:', mostRecentConversation);
        const convId = mostRecentConversation._id || mostRecentConversation.id;
        setConversationId(convId);
        setSelectedConversationId(convId);

        // Only now load messages from the most recent conversation
        await loadMessages(convId);
      } else {
        // No conversations found - clear everything and show welcome screen
        // Don't create anything, don't load any messages
        setConversationId(null);
        setSelectedConversationId(null);
        setActiveConversations([]);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to initialize conversation:', error);
      // On error, clear everything and let user start fresh
      setConversationId(null);
      setSelectedConversationId(null);
      setActiveConversations([]);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewConversation = async () => {
    if (!idToken) return null;

    try {
      const response = await axios.post(
        `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/users/conversations`,
        {
          subject: 'Support Request',
          priority: 'medium'
          // userId automatically extracted from JWT
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Extract conversation from the response data structure
      console.log('Create conversation response:', response.data);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to create new conversation:', error);
      return null;
    }
  };

  const loadMessages = async (convId) => {
    if (!idToken || !convId) return;

    try {
      const response = await axios.get(
        `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/users/conversations/${convId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            limit: 10,
            page: 1
          }
        }
      );

      // Extract messages from the response data structure
      console.log('Messages response:', response.data);
      const messages = response.data.data || response.data || [];

      // Sort messages by timestamp to show oldest first
      const sortedMessages = messages.sort(
        (a, b) =>
          new Date(a.timestamp || a.createdAt) -
          new Date(b.timestamp || b.createdAt)
      );

      const formattedMessages = sortedMessages.map((msg) => ({
        position: msg.senderId === userId ? 'right' : 'left',
        type: 'text',
        text: msg.content,
        date: new Date(msg.timestamp || msg.createdAt),
        title: msg.senderId === userId ? 'You' : 'Support'
      }));

      setMessages(formattedMessages);

      // Set initial timestamp for polling (newest message)
      if (sortedMessages.length > 0) {
        const latestMessage = sortedMessages[sortedMessages.length - 1];
        lastMessageTimestampRef.current =
          latestMessage.timestamp || latestMessage.createdAt;
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      // If conversation doesn't exist, start with empty messages
      if (error.response?.status === 404) {
        setMessages([]);
      }
    }
  };

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || newMessage.trim();

    console.log('SendMessage called:', {
      textToSend,
      conversationId,
      selectedConversationId,
      activeConversationsLength: activeConversations.length,
      idToken: !!idToken,
      userId
    });

    if (!textToSend || !conversationId || !idToken) {
      console.log('SendMessage blocked - missing required fields:', {
        hasText: !!textToSend,
        hasConversationId: !!conversationId,
        hasIdToken: !!idToken
      });
      return;
    }

    if (!messageText) setNewMessage(''); // Only clear input if not a predefined message

    // Add message to UI immediately
    const tempMessage = {
      position: 'right',
      type: 'text',
      text: textToSend,
      date: new Date(),
      title: 'You'
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      setIsLoading(true);
      console.log('Sending message to API...');

      // Try WebSocket first if connected, fallback to REST API
      let messageSent = false;
      if (wsRef.current && wsRef.current.isConnected()) {
        console.log('Attempting to send via WebSocket...');
        messageSent = wsRef.current.sendUserMessage(conversationId, textToSend);
      }

      if (!messageSent) {
        console.log('Sending via REST API...');
        const response = await axios.post(
          `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/users/conversations/${conversationId}/messages`,
          {
            content: textToSend
            // senderId automatically extracted from JWT
            // type defaults to appropriate value for users
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('Message sent successfully via REST API:', response.data);
      } else {
        console.log('Message sent successfully via WebSocket');
      }

      // Only reload messages if we used REST API (WebSocket messages come via real-time updates)
      if (!messageSent && conversationId) {
        await loadMessages(conversationId);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove the temp message and show error
      setMessages((prev) => prev.slice(0, -1));
      // You might want to show an error notification here
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = async (actionType) => {
    const messageMap = {
      technical: 'I have a technical issue / bug to report',
      event: 'I have an event related issue'
    };

    try {
      setIsLoading(true);

      // Create a new conversation when user selects an action
      if (!conversationId) {
        const newConversation = await createNewConversation();
        if (newConversation) {
          const convId = newConversation._id || newConversation.id;
          setConversationId(convId);
          setSelectedConversationId(convId);
          setActiveConversations([newConversation]);
        } else {
          console.error('Failed to create conversation for quick action');
          return;
        }
      }

      // Send the predefined message
      sendMessage(messageMap[actionType]);
    } catch (error) {
      console.error('Failed to handle quick action:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchConversation = async (newConversationId) => {
    if (newConversationId === conversationId) {
      setShowConversationList(false);
      return;
    }

    try {
      setIsLoading(true);
      setConversationId(newConversationId);
      setSelectedConversationId(newConversationId);
      setShowConversationList(false);

      // Load messages for the new conversation
      if (newConversationId) {
        await loadMessages(newConversationId);
      }
    } catch (error) {
      console.error('Failed to switch conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleConversationList = () => {
    setShowConversationList(!showConversationList);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleTyping = (value) => {
    setNewMessage(value);

    // Send typing indicator via WebSocket if connected and we have a conversation
    if (
      wsRef.current &&
      wsRef.current.isConnected() &&
      conversationId &&
      value.trim()
    ) {
      wsRef.current.sendTyping(conversationId, true);

      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to stop typing indicator after 1 second of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current && wsRef.current.isConnected() && conversationId) {
          wsRef.current.sendTyping(conversationId, false);
        }
      }, 1000);
    }
  };

  if (!isAuthenticated) {
    return null; // Don't show widget if user is not authenticated
  }

  return (
    <>
      {/* Floating Chat Button */}
      <ArgonBox
        position="fixed"
        bottom={20}
        right={20}
        zIndex={1000}
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          cursor: 'pointer',
          borderRadius: '50%',
          width: 60,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          background: 'linear-gradient(195deg, #42424a, #191919)',
          color: 'white',
          '&:hover': {
            transform: 'scale(1.05)',
            transition: 'transform 0.2s ease'
          }
        }}
      >
        <Icon fontSize="medium">{isOpen ? 'close' : 'chat'}</Icon>
      </ArgonBox>

      {/* Chat Window */}
      {isOpen && (
        <ArgonBox
          position="fixed"
          bottom={90}
          right={20}
          width={350}
          height={500}
          zIndex={999}
          sx={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            border: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Chat Header */}
          <ArgonBox
            p={2}
            sx={{
              background: 'linear-gradient(195deg, #42424a, #191919)',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              position: 'relative'
            }}
          >
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <ArgonBox display="flex" alignItems="center" gap={1}>
                <ArgonTypography variant="h6" color="white">
                  Support Chat
                </ArgonTypography>
                {/* Connection Status Indicator */}
                <Icon
                  fontSize="small"
                  sx={{
                    color: wsConnected ? '#4caf50' : '#f44336',
                    fontSize: '12px'
                  }}
                >
                  {wsConnected ? 'wifi' : 'wifi_off'}
                </Icon>
              </ArgonBox>
              <ArgonBox display="flex" alignItems="center" gap={1}>
                {/* Forum Icon - Show when multiple conversations exist */}
                {activeConversations.length > 1 && (
                  <Badge
                    badgeContent={activeConversations.length}
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={toggleConversationList}
                  >
                    <ForumIcon sx={{ color: 'white', fontSize: '20px' }} />
                  </Badge>
                )}
                <Icon
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setIsOpen(false)}
                >
                  minimize
                </Icon>
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>

          {/* Conversation List Dropdown */}
          <Collapse in={showConversationList}>
            <ArgonBox
              sx={{
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <List dense>
                {activeConversations.map((conversation, index) => (
                  <ListItem
                    key={conversation._id || conversation.id}
                    disablePadding
                  >
                    <ListItemButton
                      selected={
                        (conversation._id || conversation.id) ===
                        selectedConversationId
                      }
                      onClick={() =>
                        switchConversation(conversation._id || conversation.id)
                      }
                      sx={{
                        py: 1,
                        borderBottom:
                          index < activeConversations.length - 1
                            ? '1px solid #f0f0f0'
                            : 'none'
                      }}
                    >
                      <ListItemText
                        primary={
                          <ArgonBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <ArgonTypography
                              variant="body2"
                              fontWeight="medium"
                            >
                              {conversation.subject ||
                                `Conversation ${index + 1}`}
                            </ArgonTypography>
                            {conversation.unreadCount > 0 && (
                              <Badge
                                badgeContent={conversation.unreadCount}
                                color="error"
                                size="small"
                              />
                            )}
                          </ArgonBox>
                        }
                        secondary={
                          <ArgonTypography variant="caption" color="text">
                            {conversation.state === 'waiting_for_user'
                              ? 'Waiting for you'
                              : 'Active'}
                            {conversation.updatedAt &&
                              ` • ${new Date(
                                conversation.updatedAt
                              ).toLocaleDateString()}`}
                          </ArgonTypography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </ArgonBox>
          </Collapse>

          {/* Messages Container */}
          <ArgonBox
            ref={messageListRef}
            flex={1}
            p={1}
            sx={{
              overflowY: 'auto',
              maxHeight: '350px',
              backgroundColor: '#f8f9fa'
            }}
          >
            {isLoading && messages.length === 0 ? (
              <ArgonBox display="flex" justifyContent="center" p={3}>
                <ArgonTypography variant="body2" color="text">
                  Loading chat...
                </ArgonTypography>
              </ArgonBox>
            ) : activeConversations.length === 0 ? (
              <ArgonBox p={2}>
                {/* Welcome Message */}
                <ArgonBox
                  mb={2}
                  p={2}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <ArgonTypography variant="body2" color="text" mb={2}>
                    Hi, how can I help you?
                  </ArgonTypography>

                  {/* Quick Action Buttons */}
                  <ArgonBox display="flex" flexDirection="column" gap={1}>
                    <ArgonButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => handleQuickAction('technical')}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        borderRadius: '8px'
                      }}
                    >
                      Technical issue / bug
                    </ArgonButton>
                    <ArgonButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => handleQuickAction('event')}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        borderRadius: '8px'
                      }}
                    >
                      Event related issue
                    </ArgonButton>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            ) : messages.length === 0 && activeConversations.length > 0 ? (
              <ArgonBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={3}
                height="100%"
              >
                <ArgonTypography
                  variant="body2"
                  color="text"
                  textAlign="center"
                >
                  No messages in this conversation yet.
                  <br />
                  Start typing below to begin the conversation.
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <>
                <MessageList
                  className="message-list"
                  lockable={true}
                  toBottomHeight="100%"
                  dataSource={messages}
                />
                {/* Typing Indicator */}
                {typingUsers.size > 0 && (
                  <ArgonBox p={1} pl={2}>
                    <ArgonTypography
                      variant="caption"
                      color="text"
                      fontStyle="italic"
                    >
                      Support is typing...
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </>
            )}
          </ArgonBox>

          {/* Message Input */}
          <ArgonBox p={2} sx={{ borderTop: '1px solid #e0e0e0' }}>
            <ArgonBox display="flex" gap={1}>
              <ArgonBox flex={1}>
                <Input
                  placeholder="Type your message..."
                  multiline={false}
                  value={newMessage}
                  onChange={(e) => handleTyping(e.target.value)}
                  onKeyPress={handleKeyPress}
                  maxHeight={100}
                  referance={null}
                  clear={() => setNewMessage('')}
                  maxlength={10000}
                />
              </ArgonBox>
              <ArgonButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => sendMessage()}
                disabled={!newMessage.trim() || isLoading}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                <Icon>send</Icon>
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      )}
    </>
  );
};

export default ChatSupportWidget;
